import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import toast from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { usePermissions } from '../context/PermissionContext';
import { useDebounce } from '../hooks/useDebounce';
import { canSeeStatements } from '../config/featureFlags';

const Unallocated = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const { settings: portalSettings } = useSettings();
  const { hasPermission } = usePermissions();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  // Filter the unallocated list down to a specific document type.
  // Drives both the backend query (?documentType=...) and the column set rendered
  // below, so a statement that fell through doesn't get presented as an invoice.
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');
  const searchInputRef = useRef(null);
  const [accountNumberFilter, setAccountNumberFilter] = useState('');
  const [invoiceNumberFilter, setInvoiceNumberFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingData, setEditingData] = useState({});
  const [saving, setSaving] = useState(false);
  const initialPage = (() => { const p = parseInt(searchParams.get('page'), 10); return (!isNaN(p) && p >= 1) ? p : 1; })();
  const [pagination, setPagination] = useState({ page: initialPage, limit: 50, total: 0, pages: 0 });
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [clearAllReason, setClearAllReason] = useState('');
  const [clearingAll, setClearingAll] = useState(false);
  const [activeDataTab, setActiveDataTab] = useState('extracted'); // 'extracted' or 'json'
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showSingleDeleteModal, setShowSingleDeleteModal] = useState(false);
  const [singleDeleteReason, setSingleDeleteReason] = useState('');
  
  // Bulk allocation states
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [allocationResults, setAllocationResults] = useState(null);
  const [allocationPollingInterval, setAllocationPollingInterval] = useState(null);
  const [allocating, setAllocating] = useState(false);
  
  const debouncedSearch = useDebounce(searchQuery, 300);
  const debouncedAccountNumber = useDebounce(accountNumberFilter, 300);
  const debouncedInvoiceNumber = useDebounce(invoiceNumberFilter, 300);
  const debouncedDate = useDebounce(dateFilter, 300);

  useEffect(() => {
    fetchDocuments();
    // Clear selections when page changes or filters change
    setSelectedFiles(new Set());
  }, [pagination.page, activeSearchQuery, reasonFilter, documentTypeFilter, debouncedAccountNumber, debouncedInvoiceNumber, debouncedDate]);

  // Hydrate state from URL on mount and when browser back/forward changes the URL.
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page'), 10);
    const page = (!isNaN(pageFromUrl) && pageFromUrl >= 1) ? pageFromUrl : 1;
    const search = searchParams.get('search') || '';
    const reason = searchParams.get('failureReason') || 'all';
    const accountNumber = searchParams.get('accountNumber') || '';
    const invoiceNumber = searchParams.get('invoiceNumber') || '';
    const date = searchParams.get('date') || '';
    const docType = searchParams.get('documentType') || 'all';

    setPagination(prev => (prev.page !== page ? { ...prev, page } : prev));
    setSearchQuery(prev => (prev !== search ? search : prev));
    setActiveSearchQuery(prev => (prev !== search ? search : prev));
    setReasonFilter(prev => (prev !== reason ? reason : prev));
    setAccountNumberFilter(prev => (prev !== accountNumber ? accountNumber : prev));
    setInvoiceNumberFilter(prev => (prev !== invoiceNumber ? invoiceNumber : prev));
    setDateFilter(prev => (prev !== date ? date : prev));
    setDocumentTypeFilter(prev => (prev !== docType ? docType : prev));
  }, [searchParams]);

  // Sync state to URL when filters/pagination change (so Back from view restores filters).
  // Skip the first render so hydrate's state updates are applied before we touch the URL.
  const syncMountRef = useRef(true);
  useEffect(() => {
    if (syncMountRef.current) { syncMountRef.current = false; return; }
    const next = new URLSearchParams();
    next.set('page', String(pagination.page));
    if (activeSearchQuery && activeSearchQuery.trim()) next.set('search', activeSearchQuery.trim());
    if (reasonFilter !== 'all') next.set('failureReason', reasonFilter);
    if (documentTypeFilter !== 'all') next.set('documentType', documentTypeFilter);
    if (accountNumberFilter && accountNumberFilter.trim()) next.set('accountNumber', accountNumberFilter.trim());
    if (invoiceNumberFilter && invoiceNumberFilter.trim()) next.set('invoiceNumber', invoiceNumberFilter.trim());
    if (dateFilter && dateFilter.trim()) next.set('date', dateFilter.trim());
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [pagination.page, activeSearchQuery, reasonFilter, documentTypeFilter, accountNumberFilter, invoiceNumberFilter, dateFilter]);

  // Persist current list query for Back from view (fallback when location.state is lost)
  const returnQueryRef = useRef(searchParams.toString());
  returnQueryRef.current = searchParams.toString();
  useEffect(() => {
    try {
      sessionStorage.setItem('unallocatedReturnQuery', searchParams.toString());
    } catch (_) {}
  }, [searchParams]);

  // Ctrl+K keyboard shortcut to focus search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      
      // Check if search contains comma-separated document numbers
      let searchParam = null;
      let documentNumbersParam = null;
      
      if (activeSearchQuery && activeSearchQuery.trim()) {
        if (activeSearchQuery.includes(',')) {
          // Comma-separated document numbers - exact match
          const numbers = activeSearchQuery.split(',').map(n => n.trim()).filter(n => n);
          if (numbers.length > 0) {
            documentNumbersParam = numbers.join(',');
          }
        } else if (activeSearchQuery.trim().length >= 3) {
          // Regular search (requires 3+ chars)
          searchParam = activeSearchQuery;
        }
      }
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchParam && { search: searchParam }),
        ...(documentNumbersParam && { documentNumbers: documentNumbersParam }),
        ...(reasonFilter !== 'all' && { failureReason: reasonFilter }),
        ...(documentTypeFilter !== 'all' && { documentType: documentTypeFilter }),
        ...(debouncedAccountNumber && { accountNumber: debouncedAccountNumber }),
        ...(debouncedInvoiceNumber && { invoiceNumber: debouncedInvoiceNumber }),
        ...(debouncedDate && { date: debouncedDate })
      };
      
      const response = await api.get('/api/unallocated', { params });
      
      if (response.data && response.data.data) {
        setDocuments(response.data.data || []);
        const totalPages = response.data.pagination?.pages || 0;
        setPagination(prev => {
          const next = {
            ...prev,
            total: response.data.pagination?.total || 0,
            pages: totalPages
          };
          if (totalPages > 0 && prev.page > totalPages) {
            next.page = totalPages;
          }
          return next;
        });
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching unallocated documents:', error);
      toast.error('Error fetching documents: ' + (error.response?.data?.message || error.message));
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedDocument) return;

    try {
      setSaving(true);
      await api.put(`/api/unallocated/${selectedDocument.id}`, {
        parsedData: editingData,
        accountNumber: editingData.accountNumber
      });

      toast.success('Document updated and queued for reprocessing');
      setShowViewModal(false);
      setSelectedDocument(null);
      fetchDocuments();
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error('Error saving document: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper component for truncated cells with tooltip
  const TruncatedCell = ({ value, maxWidth = '150px', maxLength = 30 }) => {
    const displayValue = value && value !== '-' ? String(value) : '-';
    const needsTruncation = displayValue.length > maxLength;
    const truncatedValue = needsTruncation ? displayValue.substring(0, maxLength) + '...' : displayValue;
    
    return (
      <td 
        style={{ 
          maxWidth, 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          cursor: needsTruncation ? 'help' : 'default',
          position: 'relative'
        }}
        title={needsTruncation ? displayValue : ''}
      >
        {truncatedValue}
      </td>
    );
  };

  const formatSpecificReason = (reason) => {
    if (!reason) return '';
    
    const reasonMap = {
      'company_not_found': 'Company not found - Account number was extracted but no matching company exists',
      'missing_account_number': 'Account number missing - Could not extract account number from document',
      'missing_invoice_total': 'Invoice total missing - Could not extract total amount',
      'missing_invoice_number': 'Invoice number missing - Could not extract invoice number',
      'missing_vat_amount': 'VAT amount missing - Could not extract VAT amount',
      'missing_po_number': 'PO number missing - Could not extract PO number',
      'invalid_date_format': 'Invalid date format - Date could not be parsed correctly',
      'duplicate': 'Duplicate file - This file already exists in the system'
    };
    
    return reasonMap[reason] || reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all documents on current page
      const currentPageIds = new Set(documents.map(doc => doc.id));
      setSelectedFiles(prev => new Set([...prev, ...currentPageIds]));
    } else {
      // Deselect all documents on current page
      const currentPageIds = new Set(documents.map(doc => doc.id));
      setSelectedFiles(prev => {
        const newSet = new Set(prev);
        currentPageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  };

  const handleSelectFile = (fileId) => {
    const newSelected = new Set(selectedFiles);
    if (newSelected.has(fileId)) {
      newSelected.delete(fileId);
    } else {
      newSelected.add(fileId);
    }
    setSelectedFiles(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedFiles.size === 0) {
      toast.error('Please select at least one file to delete');
      return;
    }

    if (!deleteReason || deleteReason.trim().length === 0) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    try {
      setDeleting(true);
      const response = await api.delete('/api/unallocated/bulk', {
        data: { 
          fileIds: Array.from(selectedFiles),
          reason: deleteReason.trim()
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedFiles(new Set());
        setShowDeleteModal(false);
        setDeleteReason('');
        fetchDocuments();
      } else {
        toast.error(response.data.message || 'Error deleting files');
      }
    } catch (error) {
      console.error('Error bulk deleting files:', error);
      toast.error('Error deleting files: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleClearAll = async () => {
    if (!clearAllReason || clearAllReason.trim().length === 0) {
      toast.error('Please provide a reason for clearing all documents');
      return;
    }

    try {
      setClearingAll(true);
      const response = await api.delete('/api/unallocated/clear-all', {
        data: { 
          reason: clearAllReason.trim()
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setShowClearAllModal(false);
        setClearAllReason('');
        setSelectedFiles(new Set());
        fetchDocuments();
      } else {
        toast.error(response.data.message || 'Error clearing all documents');
      }
    } catch (error) {
      console.error('Error clearing all documents:', error);
      toast.error('Error clearing all documents: ' + (error.response?.data?.message || error.message));
    } finally {
      setClearingAll(false);
    }
  };

  // Bulk allocation functions
  const handleBulkAllocate = async (fileIds = []) => {
    setAllocating(true);
    setShowAllocationModal(true);
    setAllocationStatus({ processedFiles: 0, totalFiles: fileIds.length || pagination.total, status: 'processing' });
    setAllocationResults(null);

    try {
      const response = await api.post('/api/unallocated/bulk-allocate', {
        fileIds: fileIds.length > 0 ? fileIds : [] // Empty array means "all"
      });

      if (response.data.success) {
        // Start polling for status
        pollAllocationStatus(response.data.allocationId);
      } else {
        toast.error(response.data.message || 'Error starting allocation');
        setShowAllocationModal(false);
        setAllocationStatus(null);
        setAllocating(false);
      }
    } catch (error) {
      console.error('Error starting bulk allocation:', error);
      toast.error('Error starting allocation: ' + (error.response?.data?.message || error.message));
      setShowAllocationModal(false);
      setAllocationStatus(null);
      setAllocating(false);
    }
  };

  const pollAllocationStatus = async (allocationId) => {
    let resultsFetchRetries = 0;
    const MAX_RESULTS_RETRIES = 5;

    const pollOnce = async () => {
      try {
        const statusResponse = await api.get(`/api/unallocated/allocate/${allocationId}`);
        const allocationSession = statusResponse.data.allocation;

        // Check if cancelled
        if (allocationSession.cancelled || allocationSession.status === 'cancelled') {
          console.log('Allocation was cancelled');
          setAllocationStatus(null);
          setShowAllocationModal(false);
          setAllocating(false);
          if (allocationPollingInterval) {
            clearInterval(allocationPollingInterval);
            setAllocationPollingInterval(null);
          }
          toast.info('Allocation was cancelled');
          fetchDocuments();
          return true; // Stop polling
        }

        // Check if failed
        if (allocationSession.status === 'failed') {
          console.log('Allocation failed');
          setAllocationStatus(null);
          setShowAllocationModal(false);
          setAllocating(false);
          if (allocationPollingInterval) {
            clearInterval(allocationPollingInterval);
            setAllocationPollingInterval(null);
          }
          toast.error('Allocation failed. Please try again.');
          return true; // Stop polling
        }

        // Update status
        setAllocationStatus(allocationSession);

        // If processing is complete, fetch full results
        if (allocationSession.status === 'completed') {
          try {
            const resultsResponse = await api.get(`/api/unallocated/allocate/${allocationId}/results`);
            const allocationData = resultsResponse.data.allocation;
            setAllocationResults(allocationData);
            setAllocationStatus({ ...allocationSession, ...allocationData });

            // Auto-close processing modal and show results modal
            setShowAllocationModal(false);
            setAllocating(false);

            // Refresh documents list
            fetchDocuments();

            // Clear selection
            setSelectedFiles(new Set());

            return true; // Stop polling
          } catch (error) {
            console.error('Error fetching allocation results:', error);
            resultsFetchRetries++;

            // After max retries, close modal and show basic success message
            if (resultsFetchRetries >= MAX_RESULTS_RETRIES) {
              console.log('Max retries reached for fetching results, closing modal');
              setShowAllocationModal(false);
              setAllocating(false);
              fetchDocuments();
              setSelectedFiles(new Set());
              toast.success(`Allocation completed! Processed ${allocationSession.processedFiles || allocationSession.totalFiles} file(s).`);
              return true; // Stop polling
            }
            // Continue polling to retry
          }
        }

        return false; // Continue polling
      } catch (error) {
        console.error('Error polling allocation status:', error);
        setAllocationStatus(null);
        setShowAllocationModal(false);
        setAllocating(false);
        setAllocationPollingInterval(null);
        toast.error('Error checking allocation status');
        return true; // Stop polling on error
      }
    };

    // Poll immediately
    const shouldStop = await pollOnce();
    if (shouldStop) return;

    // Then poll every 1 second
    const intervalId = setInterval(async () => {
      const shouldStop = await pollOnce();
      if (shouldStop) {
        clearInterval(intervalId);
        setAllocationPollingInterval(null);
      }
    }, 1000);

    setAllocationPollingInterval(intervalId);
  };

  const handleCancelAllocation = async () => {
    if (allocationPollingInterval) {
      clearInterval(allocationPollingInterval);
      setAllocationPollingInterval(null);
    }

    // Try to cancel on the server if we have an allocation ID
    if (allocationStatus?.allocationId) {
      try {
        await api.post(`/api/unallocated/allocate/${allocationStatus.allocationId}/cancel`);
      } catch (error) {
        console.error('Error cancelling allocation:', error);
      }
    }

    setShowAllocationModal(false);
    setAllocationStatus(null);
    setAllocationResults(null);
    setAllocating(false);
    fetchDocuments();
  };

  const handleCloseAllocationResults = () => {
    setAllocationResults(null);
  };

  const hasActiveFilters = activeSearchQuery || reasonFilter !== 'all' || documentTypeFilter !== 'all';

  const handleResetFilters = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    setReasonFilter('all');
    setDocumentTypeFilter('all');
    setSelectedFiles(new Set());
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setShowSingleDeleteModal(true);
    setSingleDeleteReason('');
  };


  const confirmDeleteDocument = async () => {
    if (!documentToDelete || !singleDeleteReason || singleDeleteReason.trim().length === 0) {
      toast.error('Please provide a reason for deletion');
      return;
    }

    try {
      setDeleting(true);
      const response = await api.delete(`/api/unallocated/${documentToDelete.id}`, {
        data: { reason: singleDeleteReason.trim() }
      });

      if (response.data.success) {
        toast.success('Document deleted successfully');
        setShowSingleDeleteModal(false);
        setDocumentToDelete(null);
        setSingleDeleteReason('');
        fetchDocuments();
      } else {
        toast.error(response.data.message || 'Error deleting document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Error deleting document: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const getReasonBadge = (reason, status, metadata) => {
    // Get specific failure reason from metadata if available
    const specificReason = metadata?.specificFailureReason;
    
    // If status is unallocated, show as unallocated
    if (status === 'unallocated' || reason === 'unallocated') {
      return (
        <div>
          <span className="badge bg-warning-lt">Unallocated</span>
          {specificReason && (
            <small className="text-muted d-block mt-1">
              {formatSpecificReason(specificReason)}
            </small>
          )}
        </div>
      );
    }
    
    const classes = {
      parsing_error: 'bg-danger-lt',
      validation_error: 'bg-info-lt',
      duplicate: 'bg-secondary-lt',
      other: 'bg-dark-lt'
    };
    const label = reason ? reason.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Unknown';
    return (
      <div>
        <span className={`badge ${classes[reason] || 'bg-secondary-lt'}`}>{label}</span>
        {specificReason && (
          <small className="text-muted d-block mt-1">
            {formatSpecificReason(specificReason)}
          </small>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-header">
              <div className="row w-100 g-3">
                {/* Title and description */}
                <div className="col-lg-3 col-md-4 col-12">
                  <h3 className="card-title mb-0">Unallocated Documents</h3>
                  <p className="text-secondary m-0">Documents that could not be matched or failed processing</p>
                </div>
                {/* Controls */}
                <div className="col-lg-9 col-md-8 col-12 pe-0">
                  <div className="d-flex flex-wrap btn-list gap-2 justify-content-md-end toolbar-actions">
                    {/* Search */}
                    <div className="input-group input-group-sm input-group-flat w-auto">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Search for Documents"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.stopPropagation();
                            setActiveSearchQuery(searchQuery);
                            setPagination(prev => ({ ...prev, page: 1 }));
                          }
                        }}
                        autoComplete="off"
                      />
                      <span className="input-group-text">
                        <kbd>ctrl + K</kbd>
                      </span>
                      <button 
                        className="btn btn-sm btn-primary" 
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setActiveSearchQuery(searchQuery);
                          setPagination(prev => ({ ...prev, page: 1 }));
                        }}
                      >
                        Search
                      </button>
                    </div>
                    {/* Reason filter */}
                    <select
                      className="form-select form-select-sm w-auto"
                      value={reasonFilter}
                      onChange={(e) => {
                        setReasonFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                    >
                      <option value="all">All Reasons</option>
                      <option value="unallocated">Unallocated</option>
                      <option value="parsing_error">Parsing Error</option>
                      <option value="validation_error">Validation Error</option>
                      <option value="duplicate">Duplicate</option>
                      <option value="other">Other</option>
                    </select>
                    {/* Document type filter - drives both the query and the column set */}
                    <select
                      className="form-select form-select-sm w-auto"
                      value={documentTypeFilter}
                      onChange={(e) => {
                        setDocumentTypeFilter(e.target.value);
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                      title="Filter by document type"
                    >
                      <option value="all">All Types</option>
                      <option value="invoice">Invoices</option>
                      <option value="credit_note">Credit Notes</option>
                      {canSeeStatements(currentUser?.role, portalSettings) && <option value="statement">Statements</option>}
                      <option value="unknown">Unknown</option>
                    </select>
                    {/* Reset - only when something is filtered */}
                    {hasActiveFilters && (
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={handleResetFilters}
                        title="Reset all filters and sorting"
                      >
                        Reset
                      </button>
                    )}
                    {/* Bulk actions */}
                    {selectedFiles.size > 0 && (
                      <>
                        {hasPermission('UNALLOCATED_REALLOCATE') && (
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleBulkAllocate([...selectedFiles])}
                            disabled={allocating}
                          >
                            Allocate Selected ({selectedFiles.size})
                          </button>
                        )}
                        {hasPermission('UNALLOCATED_DELETE') && (
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              setShowDeleteModal(true);
                              setDeleteReason('');
                            }}
                            disabled={deleting}
                          >
                            Delete Selected ({selectedFiles.size})
                          </button>
                        )}
                      </>
                    )}
                    {/* Allocate All - only show when NO items selected */}
                    {hasPermission('UNALLOCATED_REALLOCATE') && pagination.total > 0 && selectedFiles.size === 0 && (
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleBulkAllocate([])}
                        disabled={allocating}
                      >
                        Allocate All ({pagination.total})
                      </button>
                    )}
                    {/* Clear All - only for users with delete permission */}
                    {hasPermission('UNALLOCATED_DELETE') && pagination.total > 0 && (
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setShowClearAllModal(true);
                          setClearAllReason('');
                        }}
                        disabled={clearingAll}
                      >
                        Clear All ({pagination.total})
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              {(() => {
                // Header set is driven by the active document-type filter so a
                // statement that fell through doesn't get presented under
                // invoice-shaped columns. 'all' uses a neutral column set that
                // works for any document type (Type, Doc No, Date, Account,
                // Amount). Specific filters get richer, type-tailored columns.
                const viewType = documentTypeFilter;
                const isStatementView = viewType === 'statement';
                const isInvoiceLikeView = viewType === 'invoice' || viewType === 'credit_note';
                const docNoLabel = viewType === 'credit_note' ? 'Credit Note No.' : 'Invoice No.';
                // Total column count = checkbox + Type + variable data cols + Status + Retention + Actions
                //   all/unknown:           1 + 1 + 4 + 3 = 9
                //   invoice/credit_note:   1 + 1 + 7 + 3 = 12
                //   statement:             1 + 1 + 8 + 3 = 13
                const totalColCount = isStatementView ? 13 : (isInvoiceLikeView ? 12 : 9);
                return (
              <table className="table table-vcenter table-selectable">
                <thead>
                  <tr>
                    <th className="w-1">
                      <input
                        type="checkbox"
                        className="form-check-input m-0 align-middle"
                        checked={documents.length > 0 && documents.every(doc => selectedFiles.has(doc.id))}
                        onChange={handleSelectAll}
                        disabled={loading || documents.length === 0}
                        title={documents.length > 0 && documents.some(doc => selectedFiles.has(doc.id)) && !documents.every(doc => selectedFiles.has(doc.id)) ? 'Some items selected' : 'Select all on this page'}
                      />
                    </th>
                    <th>Type</th>
                    {isStatementView ? (
                      <>
                        <th>Statement Date</th>
                        <th>Account No.</th>
                        <th>Total Balance</th>
                        <th>Current</th>
                        <th>1-30</th>
                        <th>31-60</th>
                        <th>61-90</th>
                        <th>91+</th>
                      </>
                    ) : isInvoiceLikeView ? (
                      <>
                        <th>{docNoLabel}</th>
                        <th>Date/Tax Point</th>
                        <th>Account No.</th>
                        <th>Invoice To</th>
                        <th>Delivery Address</th>
                        <th>PO Number</th>
                        <th>Amount</th>
                      </>
                    ) : (
                      // 'all' and 'unknown' share a neutral column set.
                      // Header lists the per-type doc-number variants so users
                      // know what the column means across mixed rows.
                      <>
                        <th>INV / CRN No.</th>
                        <th>Date</th>
                        <th>Account No.</th>
                        <th>Amount</th>
                      </>
                    )}
                    <th>Status</th>
                    <th>Retention</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody className={loading ? 'placeholder-glow' : ''}>
                  {loading ? (
                    [...Array(10)].map((_, i) => (
                      <tr key={`skeleton-${i}`}>
                        {[...Array(totalColCount)].map((__, j) => (
                          <td key={j}>
                            <span className="placeholder col-7"></span>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : documents.length === 0 ? (
                    <tr>
                      <td colSpan={totalColCount} className="text-center py-3 text-muted">
                        No unallocated documents found
                      </td>
                    </tr>
                  ) : (
                    documents.map((doc) => {
                      const parsed = doc.parsedData || {};
                      const metadataParsed = parsed.metadata?.parsedData || {};
                      // Trust the parser's canonical documentType. If it's missing
                      // (older parses, or pre-fix Excel rows) fall back to 'unknown'
                      // rather than silently calling everything an invoice — that's
                      // what produced statements showing up as Invoices on this page.
                      const rawDocType = (parsed.documentType || parsed.document_type || '').toString().toLowerCase();
                      const documentType = ['invoice', 'credit_note', 'statement'].includes(rawDocType) ? rawDocType : 'unknown';
                      const accountNumber = parsed.accountNumber || parsed.customerNumber || parsed.account_no || parsed.accountNo || '-';
                      const invoiceNumber = parsed.invoiceNumber || parsed.documentNumber || parsed.invoice_number || parsed.invoiceNo || parsed.creditNumber || parsed.credit_number || '-';
                      const invoiceTo = parsed.invoiceTo || parsed.invoice_to || parsed.bill_to || metadataParsed.invoiceTo || metadataParsed.invoice_to || '-';
                      const deliveryAddress = parsed.deliveryAddress || parsed.delivery_address || parsed.ship_to || parsed.shipping_address || metadataParsed.deliveryAddress || metadataParsed.delivery_address || '-';
                      const poNumber = parsed.customerPO || parsed.poNumber || parsed.purchaseOrder || parsed.customer_po || parsed.po_number || metadataParsed.customerPO || metadataParsed.poNumber || '-';
                      const amount = parsed.totalAmount || parsed.amount || parsed.invoiceTotal || parsed.total || metadataParsed.totalAmount || metadataParsed.amount || '-';
                      // Statement-specific fields, used by the statement view below.
                      const statementDateRaw = parsed.statementDate || parsed.statement_date || metadataParsed.statementDate || metadataParsed.statement_date || '';
                      const totalBalance = parsed.totalBalance ?? metadataParsed.totalBalance;
                      const currentAmount = parsed.currentAmount ?? metadataParsed.currentAmount;
                      const overdue1To30 = parsed.overdue1To30 ?? metadataParsed.overdue1To30;
                      const overdue31To60 = parsed.overdue31To60 ?? metadataParsed.overdue31To60;
                      const overdue61To90 = parsed.overdue61To90 ?? metadataParsed.overdue61To90;
                      const overdue91Plus = parsed.overdue91Plus ?? metadataParsed.overdue91Plus;
                      const formatMoney = (n) => {
                        if (n === undefined || n === null || n === '') return '-';
                        const num = typeof n === 'string' ? parseFloat(n.replace(/[£,\s]/g, '')) : Number(n);
                        if (!Number.isFinite(num)) return '-';
                        return `£${num.toFixed(2)}`;
                      };
                      
                      // Extract date from multiple possible field names and formats
                      const invoiceDate = parsed.invoiceDate || parsed.date || parsed.taxPoint || parsed.tax_point || 
                                         parsed.taxPointDate || parsed.tax_point_date || parsed.invoice_date ||
                                         statementDateRaw ||
                                         metadataParsed.invoiceDate || metadataParsed.date || metadataParsed.taxPoint || metadataParsed.tax_point ||
                                         metadataParsed.taxPointDate || metadataParsed.tax_point_date || metadataParsed.invoice_date || '';
                      
                      // Format date for display - handle UK format dates (dd/mm/yy or dd/mm/yyyy)
                      let formattedDate = '-';
                      if (invoiceDate) {
                        try {
                          let date = null;
                          const dateStr = String(invoiceDate).trim();
                          
                          // Try UK format first: dd/mm/yy or dd/mm/yyyy
                          const ukMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
                          if (ukMatch) {
                            let day = parseInt(ukMatch[1], 10);
                            let month = parseInt(ukMatch[2], 10);
                            let year = parseInt(ukMatch[3], 10);
                            
                            // Convert 2-digit year to 4-digit
                            if (year < 100) {
                              year = year < 50 ? 2000 + year : 1900 + year;
                            }
                            
                            // Validate and create date
                            if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                              date = new Date(year, month - 1, day);
                              if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
                                date = null; // Invalid date
                              }
                            }
                          }
                          
                          // If UK format didn't work, try standard Date parsing
                          if (!date || isNaN(date.getTime())) {
                            date = new Date(dateStr);
                          }
                          
                          if (date && !isNaN(date.getTime()) && date.getFullYear() > 1900) {
                            formattedDate = date.toLocaleDateString('en-GB', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric' 
                            });
                          }
                        } catch (e) {
                          // If parsing fails, try to display the raw value if it looks like a date
                          if (invoiceDate && invoiceDate.length > 0) {
                            formattedDate = String(invoiceDate);
                          }
                        }
                      }

                      // Format amount for display
                      let formattedAmount = '-';
                      if (amount && amount !== '-') {
                        try {
                          const amountNum = typeof amount === 'string' ? parseFloat(amount.toString().replace(/[£,]/g, '')) : amount;
                          if (!isNaN(amountNum) && amountNum > 0) {
                            formattedAmount = `£${amountNum.toFixed(2)}`;
                          }
                        } catch (e) {
                          formattedAmount = String(amount);
                        }
                      }

                      // Calculate retention date based on uploadedAt + retention period
                      let retentionInfo = '-';
                      if (doc.uploadedAt) {
                        try {
                          // For now, show uploaded date - retention calculation would need settings
                          const uploadedDate = new Date(doc.uploadedAt);
                          if (!isNaN(uploadedDate.getTime())) {
                            retentionInfo = uploadedDate.toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            });
                          }
                        } catch (e) {
                          retentionInfo = '-';
                        }
                      }

                      return (
                        <tr key={doc.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input m-0 align-middle table-selectable-check"
                              checked={selectedFiles.has(doc.id)}
                              onChange={() => handleSelectFile(doc.id)}
                            />
                          </td>
                          <td>
                            <span className={`badge ${
                              documentType === 'credit_note' ? 'bg-info-lt' :
                              documentType === 'statement' ? 'bg-secondary-lt' :
                              documentType === 'unknown' ? 'bg-muted-lt text-muted' :
                              'bg-primary-lt'
                            }`}>
                              {documentType === 'credit_note' ? 'Credit Note' :
                               documentType === 'statement' ? 'Statement' :
                               documentType === 'unknown' ? 'Unknown' :
                               'Invoice'}
                            </span>
                          </td>
                          {isStatementView ? (
                            <>
                              <td>{formattedDate}</td>
                              <td><strong>{accountNumber}</strong></td>
                              <td><strong>{formatMoney(totalBalance)}</strong></td>
                              <td>{formatMoney(currentAmount)}</td>
                              <td>{formatMoney(overdue1To30)}</td>
                              <td>{formatMoney(overdue31To60)}</td>
                              <td>{formatMoney(overdue61To90)}</td>
                              <td>{formatMoney(overdue91Plus)}</td>
                            </>
                          ) : isInvoiceLikeView ? (
                            <>
                              <td>{invoiceNumber !== '-' ? <strong>{invoiceNumber}</strong> : '-'}</td>
                              <td>{formattedDate}</td>
                              <td><strong>{accountNumber}</strong></td>
                              <TruncatedCell value={invoiceTo} maxWidth="200px" maxLength={30} />
                              <TruncatedCell value={deliveryAddress} maxWidth="200px" maxLength={40} />
                              <TruncatedCell value={poNumber} maxWidth="150px" maxLength={20} />
                              <td>{formattedAmount}</td>
                            </>
                          ) : (() => {
                            // 'all' / 'unknown' neutral view: pick the per-row best
                            // value for Amount (totalBalance for statements, totalAmount
                            // for invoices/credits). Statement rows in mixed view show '-'
                            // for Document No. since statements don't carry one.
                            const neutralAmount = documentType === 'statement'
                              ? formatMoney(totalBalance)
                              : formattedAmount;
                            const neutralDocNo = documentType === 'statement'
                              ? '-'
                              : (invoiceNumber !== '-' ? <strong>{invoiceNumber}</strong> : '-');
                            return (
                              <>
                                <td>{neutralDocNo}</td>
                                <td>{formattedDate}</td>
                                <td><strong>{accountNumber}</strong></td>
                                <td>{neutralAmount}</td>
                              </>
                            );
                          })()}
                          <td>
                            <div>
                              {getReasonBadge(doc.failureReason, doc.status, doc.metadata)}
                            </div>
                          </td>
                          <td>{retentionInfo}</td>
                          <td>
                            <div className="btn-list flex-nowrap">
                              <button 
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                const q = returnQueryRef.current || searchParams.toString();
                                try { sessionStorage.setItem('unallocatedReturnQuery', q); } catch (_) {}
                                navigate(`/unallocated/${doc.id}/view?returnQuery=${encodeURIComponent(q)}`, { state: { returnQuery: q } });
                              }}
                                title="View"
                              >
                                View
                              </button>
                              {hasPermission('UNALLOCATED_DELETE') && (
                                <button 
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteDocument(doc)}
                                  title="Delete"
                                  disabled={deleting}
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
                );
              })()}
            </div>
              
              {/* Pagination Controls */}
              {pagination.total > pagination.limit && (
                <div className="card-footer">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} Unallocated Documents
                    </div>
                    {(() => {
                      // Calculate which page numbers to show
                      const currentPage = pagination.page;
                      const totalPages = pagination.pages;
                      const pagesToShow = [];
                      
                      if (totalPages <= 7) {
                        // Show all pages if 7 or fewer
                        for (let i = 1; i <= totalPages; i++) {
                          pagesToShow.push(i);
                        }
                      } else {
                        // Show first page
                        pagesToShow.push(1);
                        
                        if (currentPage <= 4) {
                          // Near the start: 1, 2, 3, 4, 5, ..., last
                          for (let i = 2; i <= 5; i++) {
                            pagesToShow.push(i);
                          }
                          pagesToShow.push('ellipsis');
                          pagesToShow.push(totalPages);
                        } else if (currentPage >= totalPages - 3) {
                          // Near the end: 1, ..., last-4, last-3, last-2, last-1, last
                          pagesToShow.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pagesToShow.push(i);
                          }
                        } else {
                          // In the middle: 1, ..., current-1, current, current+1, ..., last
                          pagesToShow.push('ellipsis');
                          for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                            pagesToShow.push(i);
                          }
                          pagesToShow.push('ellipsis');
                          pagesToShow.push(totalPages);
                        }
                      }
                      
                      return (
                        <div className="d-flex align-items-center gap-2">
                          {/* Tabler-style pagination */}
                          <ul className="pagination m-0">
                            {/* First page button */}
                            <li className={`page-item ${pagination.page === 1 || loading ? 'disabled' : ''}`}>
                              <a 
                                className="page-link" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (pagination.page > 1 && !loading) {
                                    setPagination(prev => ({ ...prev, page: 1 }));
                                  }
                                }}
                                tabIndex={pagination.page === 1 || loading ? -1 : 0}
                                aria-disabled={pagination.page === 1 || loading}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="icon">
                                  <path d="M11 7l-5 5l5 5" />
                                  <path d="M17 7l-5 5l5 5" />
                                </svg>
                              </a>
                            </li>
                            {/* Previous page button */}
                            <li className={`page-item ${pagination.page === 1 || loading ? 'disabled' : ''}`}>
                              <a 
                                className="page-link" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (pagination.page > 1 && !loading) {
                                    setPagination(prev => ({ ...prev, page: prev.page - 1 }));
                                  }
                                }}
                                tabIndex={pagination.page === 1 || loading ? -1 : 0}
                                aria-disabled={pagination.page === 1 || loading}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="icon">
                                  <path d="M15 6l-6 6l6 6" />
                                </svg>
                              </a>
                            </li>
                            {/* Page numbers */}
                            {pagesToShow.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <li key={`ellipsis-${index}`} className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return (
                                <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                                  <a 
                                    className="page-link" 
                                    href="#" 
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (page !== currentPage && !loading) {
                                        setPagination(prev => ({ ...prev, page }));
                                      }
                                    }}
                                  >
                                    {page}
                                  </a>
                                </li>
                              );
                            })}
                            {/* Next page button */}
                            <li className={`page-item ${pagination.page >= pagination.pages || loading ? 'disabled' : ''}`}>
                              <a 
                                className="page-link" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (pagination.page < pagination.pages && !loading) {
                                    setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                                  }
                                }}
                                tabIndex={pagination.page >= pagination.pages || loading ? -1 : 0}
                                aria-disabled={pagination.page >= pagination.pages || loading}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="icon">
                                  <path d="M9 6l6 6l-6 6" />
                                </svg>
                              </a>
                            </li>
                            {/* Last page button */}
                            <li className={`page-item ${pagination.page >= pagination.pages || loading ? 'disabled' : ''}`}>
                              <a 
                                className="page-link" 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (pagination.page < pagination.pages && !loading) {
                                    setPagination(prev => ({ ...prev, page: pagination.pages }));
                                  }
                                }}
                                tabIndex={pagination.page >= pagination.pages || loading ? -1 : 0}
                                aria-disabled={pagination.page >= pagination.pages || loading}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="icon">
                                  <path d="M7 7l5 5l-5 5" />
                                  <path d="M13 7l5 5l-5 5" />
                                </svg>
                              </a>
                            </li>
                          </ul>
                          {/* Number input box */}
                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted">Page</span>
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ width: '70px' }}
                              min="1"
                              max={pagination.pages}
                              defaultValue={pagination.page}
                              key={pagination.page} // Reset input when page changes externally
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const page = parseInt(e.target.value);
                                  if (page >= 1 && page <= pagination.pages) {
                                    setPagination(prev => ({ ...prev, page }));
                                  }
                                  e.target.blur();
                                }
                              }}
                              onBlur={(e) => {
                                const page = parseInt(e.target.value);
                                if (page >= 1 && page <= pagination.pages && page !== pagination.page) {
                                  setPagination(prev => ({ ...prev, page }));
                                }
                              }}
                            />
                            <span className="text-muted">of {pagination.pages}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedDocument && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">View Document: {selectedDocument.fileName}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedDocument(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {/* Error and Failure Reason - Full Width */}
                {selectedDocument.errorMessage && (
                  <div className="alert alert-danger mb-3">
                    <strong>Error:</strong> {selectedDocument.errorMessage}
                  </div>
                )}

                {selectedDocument.failureReason && (
                  <div className="mb-3">
                    <label className="form-label">Failure Reason <span className="text-muted">(Read-only)</span></label>
                    <div>
                      {getReasonBadge(selectedDocument.failureReason, selectedDocument.status, selectedDocument.metadata)}
                    </div>
                    {selectedDocument.metadata?.specificFailureReason && (
                      <small className="text-muted d-block mt-2">
                        <strong>What to fix:</strong> {formatSpecificReason(selectedDocument.metadata.specificFailureReason)}
                      </small>
                    )}
                    {selectedDocument.metadata?.missingFields && selectedDocument.metadata.missingFields.length > 0 && (
                      <small className="text-muted d-block mt-1">
                        <strong>Missing fields:</strong> {selectedDocument.metadata.missingFields.join(', ')}
                      </small>
                    )}
                  </div>
                )}

                {/* Two Column Layout: PDF on Left, Extracted Data Tabs on Right */}
                <div className="row">
                  {/* Left Column: PDF Preview */}
                  <div className="col-lg-6 mb-3">
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">PDF Preview</h3>
                      </div>
                      <div className="card-body p-0" style={{ height: '600px', overflow: 'auto' }}>
                        <iframe
                          src={`/uploads/${selectedDocument.filePath.replace(/^.*[\\/]/, '')}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none'
                          }}
                          title="PDF Preview"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Tabbed Extracted Data */}
                  <div className="col-lg-6 mb-3">
                    <div className="card">
                      <div className="card-header">
                        <ul className="nav nav-tabs card-header-tabs">
                          <li className="nav-item">
                            <button
                              className={`nav-link ${activeDataTab === 'extracted' ? 'active' : ''}`}
                              onClick={() => setActiveDataTab('extracted')}
                            >
                              Extracted Data
                            </button>
                          </li>
                          {currentUser?.role === 'global_admin' && selectedDocument.parsedData && (
                            <li className="nav-item">
                              <button
                                className={`nav-link ${activeDataTab === 'json' ? 'active' : ''}`}
                                onClick={() => setActiveDataTab('json')}
                              >
                                JSON
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                      <div className="card-body">
                        <div className="tab-content">
                          {/* Extracted Data Tab */}
                          <div className={`tab-pane ${activeDataTab === 'extracted' ? 'active show' : ''}`} id="extracted-tab">
                            <div className="mb-3">
                              <label className="form-label">Document Type</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingData.documentType || ''}
                                onChange={(e) => setEditingData(prev => ({ ...prev, documentType: e.target.value }))}
                                placeholder="invoice, credit_note, statement"
                                style={{ textTransform: 'capitalize' }}
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label required">Account Number / Customer Number</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingData.accountNumber || ''}
                                onChange={(e) => setEditingData(prev => ({ ...prev, accountNumber: e.target.value }))}
                                placeholder="Enter account number"
                              />
                              <small className="form-hint">This must match a company's Account / Company Number</small>
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Date / Tax Point</label>
                              <input
                                type="date"
                                className="form-control"
                                value={(() => {
                                  const dateValue = editingData.invoiceDate || editingData.date || editingData.taxPoint;
                                  if (!dateValue) return '';
                                  try {
                                    const date = new Date(dateValue);
                                    if (isNaN(date.getTime())) return '';
                                    return date.toISOString().split('T')[0];
                                  } catch {
                                    return '';
                                  }
                                })()}
                                onChange={(e) => {
                                  const value = e.target.value ? new Date(e.target.value).toISOString() : null;
                                  setEditingData(prev => ({ 
                                    ...prev, 
                                    invoiceDate: value,
                                    date: value,
                                    taxPoint: value
                                  }));
                                }}
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Invoice Number</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingData.invoiceNumber || editingData.documentNumber || ''}
                                onChange={(e) => setEditingData(prev => ({ 
                                  ...prev, 
                                  invoiceNumber: e.target.value,
                                  documentNumber: e.target.value 
                                }))}
                                placeholder="Enter invoice number"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">PO Number</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingData.customerPO || editingData.poNumber || ''}
                                onChange={(e) => setEditingData(prev => ({ 
                                  ...prev, 
                                  customerPO: e.target.value,
                                  poNumber: e.target.value 
                                }))}
                                placeholder="Enter PO number"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Total</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={editingData.totalAmount || editingData.amount || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || null;
                                  setEditingData(prev => ({ 
                                    ...prev, 
                                    totalAmount: value,
                                    amount: value 
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">VAT Amount</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={editingData.vatAmount || editingData.vatTotal || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || null;
                                  setEditingData(prev => ({ 
                                    ...prev, 
                                    vatAmount: value,
                                    vatTotal: value 
                                  }));
                                }}
                                placeholder="0.00"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Delivery Address</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                value={editingData.deliveryAddress || ''}
                                onChange={(e) => setEditingData(prev => ({ ...prev, deliveryAddress: e.target.value }))}
                                placeholder="Enter delivery address"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Goods Amount</label>
                              <input
                                type="number"
                                step="0.01"
                                className="form-control"
                                value={editingData.goodsAmount || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || null;
                                  setEditingData(prev => ({ ...prev, goodsAmount: value }));
                                }}
                                placeholder="0.00"
                              />
                            </div>

                            <div className="mb-3">
                              <label className="form-label">Invoice To</label>
                              <input
                                type="text"
                                className="form-control"
                                value={editingData.invoiceTo || ''}
                                onChange={(e) => setEditingData(prev => ({ ...prev, invoiceTo: e.target.value }))}
                                placeholder="Enter invoice to address or name"
                              />
                            </div>

                            {selectedDocument.editLog && selectedDocument.editLog.length > 0 && (
                              <div className="mb-3">
                                <label className="form-label">Edit History</label>
                                <div className="card">
                                  <div className="card-body">
                                    {selectedDocument.editLog.map((log, idx) => (
                                      <div key={idx} className="mb-2 pb-2 border-bottom">
                                        <div className="text-muted small">
                                          <strong>{log.editedByName}</strong> - {formatDate(log.editedAt)}
                                        </div>
                                        {log.changes && Object.keys(log.changes).length > 0 && (
                                          <div className="small mt-1">
                                            {Object.entries(log.changes).map(([key, change]) => (
                                              <div key={key}>
                                                <strong>{key}:</strong> {JSON.stringify(change.from)} → {JSON.stringify(change.to)}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* JSON Tab - Only for Global Admins */}
                          {currentUser?.role === 'global_admin' && selectedDocument.parsedData && (
                            <div className={`tab-pane ${activeDataTab === 'json' ? 'active show' : ''}`} id="json-tab">
                              <div className="mb-3">
                                <label className="form-label">
                                  <strong>Parsed JSON from Template</strong> <span className="text-muted">(Global Admin Only)</span>
                                </label>
                                <div className="card">
                                  <div className="card-body p-0">
                                    <pre className="mb-0" style={{ 
                                      maxHeight: '500px', 
                                      overflow: 'auto', 
                                      fontSize: '12px',
                                      backgroundColor: '#ffffff',
                                      color: '#1f2937',
                                      padding: '1rem',
                                      borderRadius: '4px',
                                      margin: 0
                                    }}>
                                      {JSON.stringify(selectedDocument.parsedData, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                                <small className="form-hint text-muted d-block mt-2">
                                  This shows the raw JSON data extracted from the template. Use this to verify what fields were parsed and their values.
                                </small>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedDocument(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save & Requeue'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear All Confirmation Modal */}
      {showClearAllModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-danger me-2"></i>
                  Clear All Unallocated Documents
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowClearAllModal(false);
                    setClearAllReason('');
                  }}
                  disabled={clearingAll}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <strong>Warning:</strong> This action cannot be undone. All unallocated documents and their associated database records will be permanently deleted.
                </div>
                <p>
                  You are about to delete <strong>ALL unallocated documents</strong> from the system. This will:
                </p>
                <ul>
                  <li>Delete all unallocated files from disk</li>
                  <li>Delete all database records (File records with status: unallocated, failed, duplicate)</li>
                  <li>Delete any associated Invoice or CreditNote records</li>
                </ul>
                <div className="mb-3 mt-3">
                  <label className="form-label required">
                    <strong>Reason for clearing all documents <span className="text-danger">*</span></strong>
                  </label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Please provide a reason for this action..."
                    value={clearAllReason}
                    onChange={(e) => setClearAllReason(e.target.value)}
                    disabled={clearingAll}
                    required
                  />
                  <small className="form-hint">
                    This reason will be recorded in the activity log for accountability purposes.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => {
                    setShowClearAllModal(false);
                    setClearAllReason('');
                  }}
                  disabled={clearingAll}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary" 
                  onClick={handleClearAll}
                  disabled={clearingAll || !clearAllReason || clearAllReason.trim().length === 0}
                >
                  {clearingAll ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Clearing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-trash me-2"></i>
                      Clear All
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Confirmation Modal */}
      {showSingleDeleteModal && documentToDelete && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Document</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSingleDeleteModal(false);
                    setDocumentToDelete(null);
                    setSingleDeleteReason('');
                  }}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="text-danger">
                    <strong>Warning:</strong> This action cannot be undone. The document <strong>{documentToDelete.fileName}</strong> and its associated physical file will be permanently deleted.
                  </p>
                </div>
                <div className="mb-3">
                  <label className="form-label required">Reason for Deletion</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Please provide a reason for deleting this document (required for accountability)..."
                    value={singleDeleteReason}
                    onChange={(e) => setSingleDeleteReason(e.target.value)}
                    disabled={deleting}
                    required
                  />
                  <small className="form-hint">
                    This reason will be recorded in the system for accountability purposes.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowSingleDeleteModal(false);
                    setDocumentToDelete(null);
                    setSingleDeleteReason('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmDeleteDocument}
                  disabled={deleting || !singleDeleteReason.trim()}
                >
                  {deleting ? 'Deleting...' : 'Delete Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Unallocated Files</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteReason('');
                  }}
                  disabled={deleting}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <p className="text-danger">
                    <strong>Warning:</strong> This action cannot be undone. The files and all associated database records will be permanently deleted.
                  </p>
                  <p>
                    You are about to delete <strong>{selectedFiles.size} file(s)</strong>. This will:
                  </p>
                  <ul>
                    <li>Delete the file(s) from disk</li>
                    <li>Delete all database records (File, Invoice, CreditNote)</li>
                  </ul>
                </div>
                <div className="mb-3">
                  <label className="form-label required">Reason for Deletion</label>
                  <textarea
                    className="form-control"
                    rows="4"
                    placeholder="Please provide a reason for deleting these files (required for accountability)..."
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={deleting}
                    required
                  />
                  <small className="form-hint">
                    This reason will be recorded in the system for each deleted file for accountability purposes.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteReason('');
                  }}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={handleBulkDelete}
                  disabled={deleting || !deleteReason.trim()}
                >
                  {deleting ? 'Deleting...' : `Delete ${selectedFiles.size} File(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allocation Processing Modal */}
      {showAllocationModal && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Allocating Documents</h5>
                <button type="button" className="btn-close" onClick={handleCancelAllocation}></button>
              </div>
              <div className="modal-body text-center py-4">
                <div className="alert alert-info mb-3">
                  <strong>Note:</strong> The system will automatically detect whether each document is an Invoice or Credit Note and route it to the appropriate screen.
                </div>
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Processing...</span>
                </div>
                <h3>Processing {allocationStatus?.totalFiles || 0} file(s)...</h3>
                <p className="text-muted">
                  Processed {allocationStatus?.processedFiles || 0} of {allocationStatus?.totalFiles || 0} files
                </p>
                {allocationStatus?.currentFile && (
                  <p className="text-muted small">
                    Current: {allocationStatus.currentFile}
                  </p>
                )}
                {allocationStatus && allocationStatus.totalFiles > 0 && (
                  <div className="progress">
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      style={{ width: `${((allocationStatus?.processedFiles || 0) / allocationStatus.totalFiles) * 100}%` }}
                    >
                      {Math.round(((allocationStatus?.processedFiles || 0) / allocationStatus.totalFiles) * 100)}%
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-danger" onClick={handleCancelAllocation}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Allocation Results Modal */}
      {allocationResults && (
        <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header bg-success-lt">
                <h5 className="modal-title">
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-md me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M5 12l5 5l10 -10"/>
                  </svg>
                  Allocation Complete
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseAllocationResults}></button>
              </div>
              <div className="modal-body">
                {/* Summary Cards */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card card-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <span className="bg-primary text-white avatar me-3">
                            {allocationResults.totalFiles}
                          </span>
                          <div>
                            <div className="font-weight-medium">Total Files</div>
                            <div className="text-muted">Processed</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card card-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <span className="bg-success text-white avatar me-3">
                            {allocationResults.summary?.successful || 0}
                          </span>
                          <div>
                            <div className="font-weight-medium">Successful</div>
                            <div className="text-muted">Allocated</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card card-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <span className="bg-danger text-white avatar me-3">
                            {allocationResults.summary?.failed || 0}
                          </span>
                          <div>
                            <div className="font-weight-medium">Failed</div>
                            <div className="text-muted">Not allocated</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card card-sm">
                      <div className="card-body">
                        <div className="d-flex align-items-center">
                          <span className="bg-info text-white avatar me-3">
                            {(allocationResults.summary?.invoices || 0) + (allocationResults.summary?.creditNotes || 0)}
                          </span>
                          <div>
                            <div className="font-weight-medium">
                              {allocationResults.summary?.invoices || 0} Inv / {allocationResults.summary?.creditNotes || 0} CN
                            </div>
                            <div className="text-muted">Created</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Results Table */}
                <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  <table className="table table-vcenter table-striped">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Status</th>
                        <th>Document Type</th>
                        <th>Document Number</th>
                        <th>Company</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allocationResults.results?.map((result, index) => (
                        <tr key={index}>
                          <td>
                            <span className="text-truncate" style={{ maxWidth: '200px', display: 'inline-block' }} title={result.fileName}>
                              {result.fileName}
                            </span>
                          </td>
                          <td>
                            {result.success ? (
                              <span className="badge bg-success-lt text-success">Success</span>
                            ) : (
                              <span className="badge bg-danger-lt text-danger">Failed</span>
                            )}
                          </td>
                          <td>
                            {result.success && (
                              <span className={`badge ${result.documentType === 'invoice' ? 'bg-blue-lt text-blue' : 'bg-purple-lt text-purple'}`}>
                                {result.documentType === 'invoice' ? 'Invoice' : 'Credit Note'}
                              </span>
                            )}
                          </td>
                          <td>{result.documentNumber || '-'}</td>
                          <td>{result.companyName || '-'}</td>
                          <td>
                            {result.error && (
                              <span className="text-danger small" title={result.error}>
                                {result.error.length > 50 ? result.error.substring(0, 50) + '...' : result.error}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={handleCloseAllocationResults}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Unallocated;
